"use client";
// DEC-0328 · כניסת צוות.
//
// Gal, 2026-08-23: "פשוט זה יעבוד רק למי שנמצא תחת הדומיין של gam.co.il. מי שלא —
// זה כאילו הוא מתחבר לאתר פשוט באמצעות google הרגיל."
//
// So this page does NOT reject anyone. It is the same Google sign-in as the
// public page, with `hd` set so Google offers only work accounts first. What
// makes it "team entry" happens after: an @gam.co.il address is already
// approved and carries the device-key policy; anything else lands in the
// waiting queue like any other registration. One door, two outcomes — which is
// why a refusal screen here would only be a second place to maintain.
import { useState } from "react";
import Link from "next/link";
import { supabase } from "@/lib/supabaseClient";

const TEAM_DOMAIN = "gam.co.il";

export default function TeamLoginPage() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signIn() {
    setBusy(true);
    setError(null);
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        // A hint, not a gate: Google surfaces the work account first. The
        // actual decision is the access_status the person already carries.
        queryParams: { hd: TEAM_DOMAIN, prompt: "select_account" },
      },
    });
    if (authError) {
      setError(authError.message);
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--theme-bg)] flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-sm text-center">
        <h1 className="text-2xl font-bold text-[var(--theme-text)] mb-2">כניסת צוות</h1>
        <p className="text-sm text-[var(--theme-text-muted)] mb-8">
          לחשבונות {TEAM_DOMAIN}
        </p>

        {error && (
          <div className="mb-4 rounded-md bg-[color-mix(in_srgb,var(--role-error)_12%,transparent)] px-3 py-2 text-[12px] text-[var(--role-error)]">
            {error}
          </div>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={signIn}
          className="cta-button w-full inline-flex items-center justify-center gap-3 disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.66 2.84C6.71 7.29 9.14 5.38 12 5.38z" />
          </svg>
          כניסה עם גוגל
        </button>

        <div className="mt-8 pt-6 border-t border-[var(--theme-border)]">
          <Link href="/login" className="text-xs text-[var(--theme-text-faint)] hover:text-[var(--theme-text-muted)]">
            אינך מהצוות? לכניסה הרגילה
          </Link>
        </div>
      </div>
    </div>
  );
}
